import { Col, Row } from 'antd';
import { NotificationType } from '@app/components/common/Notification/Notification';
import { CurrencyType } from '@app/interfaces/interfaces';
import { Priority } from '@app//constants/enums/priorities';
import { ReactComponent as ETHIcon } from '@app/assets/icons/eth.svg';
import { ReactComponent as BTCIcon } from '@app/assets/icons/btc.svg';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { MOCK_TEST_TYPE } from '@app/constants/mockTest';
import {
  FULL_TEST_STEP_DURATIONS,
  FULL_TEST_STEPS,
  SECTION_TEST_STEP_DURATIONS,
  SECTION_TEST_STEPS,
} from '@app/components/exam/ExamMockTest/ExamMockTestDetail/mockTestSteps';

export const camelize = (string: string): string => {
  return string
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)))
    .join('');
};

export const getCurrencyPrice = (
  price: number | string,
  currency: CurrencyType,
  isIcon = true,
): string | React.ReactNode => {
  switch (currency) {
    case 'USD': {
      return isIcon ? `$${price}` : `${price} USD`;
    }

    case 'BTC': {
      return isIcon ? (
        <Row align="middle" gutter={[8, 8]}>
          <Col style={{ display: 'flex' }}>
            <BTCIcon />
          </Col>

          <Col>{price}</Col>
        </Row>
      ) : (
        `${price} BTC`
      );
    }

    case 'ETH': {
      return isIcon ? (
        <Row align="middle" gutter={[8, 8]}>
          <Col style={{ display: 'flex' }}>
            <ETHIcon />
          </Col>

          <Col>{price}</Col>
        </Row>
      ) : (
        `${price} ETH`
      );
    }

    default: {
      return isIcon ? `$${price}` : `${price} USD`;
    }
  }
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((el, index) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return `${r}, ${g}, ${b}`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : '100%';

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === 'number' ? `${prop}px` : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

export const defineColorByPriority = (priority: Priority): string => {
  switch (priority) {
    case Priority.INFO:
      return 'var(--primary-color)';
    case Priority.LOW:
      return 'var(--success-color)';
    case Priority.MEDIUM:
      return 'var(--warning-color)';
    case Priority.HIGH:
      return 'var(--error-color)';
    default:
      return 'var(--success-color)';
  }
};

export const defineColorBySeverity = (severity: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (severity) {
    case 'error':
    case 'warning':
    case 'success':
      return `var(--${severity}-${postfix})`;
    case 'info':
    default:
      return `var(--primary-${postfix})`;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString());
  G = parseInt(((G * (100 + percent)) / 100).toString());
  B = parseInt(((B * (100 + percent)) / 100).toString());

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max == min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return {
      h,
      s,
      l,
    };
  } else {
    throw new Error('Non valid HEX color');
  }
};

export const formatNumberWithCommas = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export const elementClasses = (...className: string[]): string => className.filter(Boolean).join(' ');

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const convertToAbbreviation = (input: string): string => {
  return input
    .split('_')
    .map((word) => word.charAt(0))
    .join('');
};

export const convertTypeParctice = (input: string): string => {
  const conversionMap: Record<string, string> = {
    LISTENING_FIB_L: 'Fill in the blanks [LFIB]',
    LISTENING_HIGHLIGHT_INCORRECT_WORD: 'Highlight incorrect words',
    LISTENING_MCQ_L_SINGLE_ANSWER: 'MC, choose single answer',
    LISTENING_SUMMARIZE_SPOKEN_TEXT: 'Summarize spoken text',
    LISTENING_DICTATION: 'Write from dictation [WFD]',
    LISTENING_SELECT_MISSING_WORD: 'Select missing words',
    LISTENING_HIGHLIGHT_CORRECT_SUMMARY: 'Highlight correct summary',
    LISTENING_MCQ_L_MULTIPLE_ANSWER: 'MC, choose multiple answers',
    SPEAKING_REPEAT_SENTENCE: 'Repeat sentence',
    SPEAKING_RETELL_LECTURE: 'Retell lecture',
    SPEAKING_DESCRIBE_IMAGE: 'Describe image',
    SPEAKING_READ_ALOUD: 'Read aloud',
    SPEAKING_ANSWER_SHORT_QUESTION: 'Answer short question',
    WRITING_ESSAY: 'Write essay [WE]',
    WRITING_SUMMARIZE_WRITTEN_TEXT: 'Summarize written text [SWT]',
    READING_FIB_R: 'R: Fill in the blanks [RFIB]',
    READING_FIB_R_W: 'R&W: Fill in the blanks [RWFIB]',
    READING_MCQ_R_MULTIPLE_ANSWER: 'MC, choose multiple answers',
    READING_MCQ_R_SINGLE_ANSWER: 'MC, choose single answer',
    READING_RE_ORDER_PARAGRAPH: 'Re-order paragraphs',
  };

  return conversionMap[input] || input;
};

export const statusType: { [key: string]: string } = {
  Pending: 'warning',
  Done: 'success',
  Failed: 'error',
  Cancelled: 'default',
  NoAnswer: 'error',
};

export const statusTypePractice: { [key: string]: string } = {
  NOT_SCORED: 'default',
  SCORING: 'warning',
  SCORED: 'success',
};

export const getInitialStepAndTime = (
  mockTestType: string,
  mockTestSection?: string,
): { step: string; remainTime: number } => {
  if (mockTestType === MOCK_TEST_TYPE.FULL_TEST) {
    const step = FULL_TEST_STEPS[0];
    const remainTime = FULL_TEST_STEP_DURATIONS[step];
    return { step, remainTime };
  }

  if (
    (mockTestType === MOCK_TEST_TYPE.SECTION_TEST || mockTestType === MOCK_TEST_TYPE.QUESTION_TEST) &&
    mockTestSection
  ) {
    const steps = SECTION_TEST_STEPS[mockTestSection];
    const durations = SECTION_TEST_STEP_DURATIONS[mockTestSection];
    if (steps?.length && durations) {
      const step = steps[0];
      const remainTime = durations[step];
      return { step, remainTime };
    }
  }

  return { step: '', remainTime: 0 };
};
